/* global ifCl */
import { safeStr, block, cliCommand } from "common/api";
import {
  ensure,
  wrapErrors,
  asAString,
  notEmpty,
  noLongerThan,
} from "./composition";
import { enumerate } from "common/utils";
export { NothingToDo } from "common/api";

const configureBlock = block({
  optional: true,
  start: "configure",
  end: "commit",
  enter: false,
});

export const commandsBlock = block({ enter: true });

const asSuppresionCommand = ({ cliType }, { name }) =>
  `no profile ${cliType} ${safeStr(name)}`;

const asClearCommand = ({ cliType }, { name }) =>
  `clear profile ${cliType} ${safeStr(name)}`;

const valuesAsString = ({ added = [], updated = [] }) =>
  [...added, ...updated].join(" ");

const validateSetSingleValueGiven =
  ({ validate = asIts }) =>
  (value) =>
    validate(value);

const validateSetListOfValuesGiven = (terms) => (items) =>
  validateItemsBy(terms)(splitByActions(items));

const validateSetValueGiven = ({ type, ...composition }) =>
  type === "many"
    ? validateSetListOfValuesGiven(composition)
    : validateSetSingleValueGiven(composition);

const fieldsExpressionMayFailIn = (profileName) => (doFields) => {
  try {
    return doFields();
  } catch (fieldsError) {
    throw `Profile "${profileName}" ${fieldsError}`;
  }
};
const fieldValueGivenProfile =
  (profile) =>
  ({
    name: field,
    cliExpressAs: expressFieldAs = valuesAsString,
    ...composition
  }) =>
    expressFieldAs(validateSetValueGiven(composition)(profile[field]));

const fieldsDefinition = (fields, profile) =>
  fieldsExpressionMayFailIn(profile.name)(() =>
    fields.filter(notItsName).map(fieldValueGivenProfile(profile)).join(" ")
  );

const asSetCommand = ({ cliType, fields }, profile) =>
  `set profile ${cliType} ${safeStr(
    validateProfileName(profile.name)
  )} ${fieldsDefinition(fields, profile)}`;

const PROFILE_LENGTH_MAX = 31;

const validateProfileName = wrapErrors(
  (error) => `Profile name ${error}`,
  ensure(asAString(notEmpty, noLongerThan(PROFILE_LENGTH_MAX)))
);

export const profileSettingsBlockOf = ({ name, type }) =>
  block({
    start: `profile ${type} ${safeStr(name)}`,
    end: "root",
    enter: false,
    optional: true,
  });

const asDefinitionCommand = (
  { cliType, type, fields = [] },
  { name, ...current }
) =>
  profileSettingsBlockOf({
    name: validateProfileName(name),
    type: cliType || type,
  })(
    fieldsExpressionMayFailIn(name)(() => [
      ...fields
        .filter(notItsName)
        .map((field) => expressPropertyChange(field, current, {})),
    ])
  );

const expressPropertyItemsChange = ({ name, ...terms }, current, previous) =>
  previous !== null &&
  JSON.stringify(current[name]) === JSON.stringify(previous[name])
    ? []
    : composePropertyItemsChange(
        { name, ...terms },
        current[name],
        previous[name]
      );

const mayWrap = (wrap, value) => (wrap === true ? safeStr(value) : value);

const valuesOfItem = (fields = [], item) =>
  fields
    .map(({ name, wrapSafe = false, validate = alwaysOk }) =>
      mayWrap(wrapSafe, validate(item[name]))
    )
    .join(" ");

const asClearItemWith =
  ({ name, cliName, fields = [] }) =>
  (item) =>
    `no ${cliName || name} ${valuesOfItem(fields, item)}`;

const asAssertItemWith =
  ({ name, cliName, fields = [] }) =>
  (item) =>
    `${cliName || name} ${valuesOfItem(fields, item)}`;

const updateExistingItem = (terms, current, previous) => [
  ...(previous === null ? [] : [asClearItemWith(terms)(previous)]),
  asAssertItemWith(terms)(current),
];

const getOriginalItem = ({ __id = undefined }, previous = []) =>
  previous.find(
    (current) => current.__id !== undefined && current.__id === __id
  ) || null;

const validateItemsBy =
  ({ validate = null, canBeEmpty = true, label }) =>
  ({ added, updated, deleted, keeped }) => {
    if (canBeEmpty !== true || validate !== null) {
      const definitions = [...added, ...updated];
      if (canBeEmpty !== true) {
        if (definitions.length === 0 && keeped.length === 0) {
          throw `has no ${label}`;
        }
      }
      if (validate !== null) {
        const others = [...keeped, ...definitions];
        definitions.forEach((item) => validate(item, others));
      }
    }
    return { added, updated, deleted, keeped };
  };

const splitByActions = (items = []) =>
  items.reduce(
    ({ added = [], deleted = [], updated = [], keeped = [] }, current) =>
      isMarkedAsDeletedButStored(current)
        ? { added, updated, keeped, deleted: [...deleted, current] }
        : isMarkedAsNotStoredNotDeleted(current)
        ? { deleted, updated, keeped, added: [...added, current] }
        : isMarkedAsModifiedAndStoredNotDeleted(current)
        ? { added, deleted, keeped, updated: [...updated, current] }
        : { added, deleted, updated, keeped: [...keeped, current] },
    { added: [], deleted: [], updated: [], keeped: [] }
  );

const appendPrevious = (updated, previous) =>
  updated.map((update) => ({
    from: getOriginalItem(update, previous),
    to: update,
  }));

const composePropertyItemsChange = (
  { cliExpressAs, ...terms },
  items,
  previous = []
) => {
  const { added, deleted, updated, keeped } = validateItemsBy(terms)(
    splitByActions(items)
  );
  return cliExpressAs === undefined
    ? [
        ...deleted.map(asClearItemWith(terms)),
        ...added.map(asAssertItemWith(terms)),
        ...updated.map((updated) =>
          updateExistingItem(terms, updated, getOriginalItem(updated, previous))
        ),
      ]
    : [
        ...cliExpressAs({
          added,
          deleted,
          updated: appendPrevious(updated, previous),
          keeped,
        }),
      ];
};

const getOriginalProfile = ({ name, __id = undefined }, previous = []) =>
  previous.find(
    (current) =>
      (current.__id !== undefined && current.__id === __id) ||
      (current.name !== undefined && current.name === name)
  ) || null;

const declarePropertyValue = (name, value, safeWrap = false) =>
  `${name} ${safeWrap === true ? safeStr(value) : value}`;

const alwaysOk = (value) => value;

const expressPropertyValueChange = (
  { validate = alwaysOk, name, safeWrap },
  current,
  previous
) =>
  current[name] === previous[name]
    ? []
    : declarePropertyValue(name, validate(current[name]), safeWrap);

const expressPropertyChange = ({ type, ...terms }, current, previous) =>
  type === "many"
    ? expressPropertyItemsChange(terms, current, previous)
    : expressPropertyValueChange(terms, current, previous);

const notItsName = ({ name }) => name !== "name";

const asUpdateCommands = (
  { cliType, type, fields = [] },
  { name, ...current },
  previous = {}
) =>
  profileSettingsBlockOf({ name, type: cliType || type })([
    ...fields
      .filter(notItsName)
      .map((field) => expressPropertyChange(field, current, previous)),
  ]);

const skipDeletedItemsMarkAsNotStored = ({ fields }, target) =>
  Object.fromEntries(
    fields.map(({ name: key, type }) => [
      key,
      type === "many"
        ? target[key]
            .filter(({ deleted = false }) => deleted !== true)
            .map((item) => ({ ...item, stored: false }))
        : target[key],
    ])
  );

const deleteAndDeclare = (composition, updated) => [
  ...updated.map((remove) => asSuppresionCommand(composition, remove)),
  ...updated.map((added) =>
    asDefinitionCommand(
      composition,
      skipDeletedItemsMarkAsNotStored(composition, added)
    )
  ),
];

const expressUpdateCommands = (
  { redeclareToUpdate = false, ...composition },
  updated,
  previous
) =>
  redeclareToUpdate === true
    ? deleteAndDeclare(composition, updated)
    : updated.map((update) =>
        asUpdateCommands(
          composition,
          update,
          getOriginalProfile(update, previous)
        )
      );

const expressAsConfigureCommand = (
  composition,
  previous,
  { added = [], deleted = [], updated = [] }
) =>
  configureBlock([
    ...deleted.map((remove) => asSuppresionCommand(composition, remove)),
    ...expressUpdateCommands(composition, updated, previous),
    ...added.map((added) => asDefinitionCommand(composition, added)),
  ]);

const expressAsScriptCommands = (
  { expectClear, expectSet, ...composition },
  previous,
  { added = [], deleted = [], updated = [] }
) => [
  ...(expectClear
    ? deleted.map((remove) => asClearCommand(composition, remove))
    : []),
  ...(expectSet
    ? [...added, ...updated].map((definition) =>
        asSetCommand(composition, definition)
      )
    : []),
  ...expressAsConfigureCommand(composition, previous, {
    added: expectSet ? [] : added,
    updated: expectSet ? [] : updated,
    deleted: expectClear ? [] : deleted,
  }),
];

const isMarkedAsDeletedButStored = ({ deleted, stored }) =>
  deleted === true && stored === true;

const isMarkedAsModifiedAndStoredNotDeleted = ({ modified, deleted, stored }) =>
  deleted !== true && stored === true && modified === true;

const isMarkedAsNotStoredNotDeleted = ({ stored, deleted }) =>
  deleted !== true && stored !== true;

const nothingIsStored = (target) =>
  Object.fromEntries(
    Object.entries(target).map(([key, value]) => [
      key,
      key === "stored"
        ? false
        : Array.isArray(value)
        ? value.map(nothingIsStored)
        : typeof value === "object"
        ? nothingIsStored(value)
        : value,
    ])
  );

const differentName = (one, other) => one.name !== other.name;

const mayBeARenameSoDeleteAndCreate = (current, previous) =>
  isMarkedAsModifiedAndStoredNotDeleted(current) &&
  previous !== null &&
  differentName(current, previous)
    ? [
        { ...previous, deleted: true },
        { ...nothingIsStored(current), modified: false, created: true },
      ]
    : null;

const treatRenameAsDeleteAndCreate = (expected = [], previous = []) =>
  expected.flatMap(
    (current) =>
      mayBeARenameSoDeleteAndCreate(
        current,
        getOriginalProfile(current, previous)
      ) || [current]
  );

const expressCommandsGiven = ({ expectClear = false, expectSet = false }) =>
  expectClear !== false || expectSet !== false
    ? expressAsScriptCommands
    : expressAsConfigureCommand;

export const expressApplyCommand = (composition, previous, expected) =>
  cliCommand`
    ${commandsBlock(
      expressCommandsGiven(composition)(
        composition,
        previous,
        splitByActions(treatRenameAsDeleteAndCreate(expected, previous))
      )
    ).join("\n")}`(false /*enter*/);

const enumeratedAndStored = (values = []) =>
  enumerate(0, { stored: true })(values);

const savedNestedItems = (source, fields = []) =>
  Object.fromEntries(
    fields
      .filter(({ type }) => type === "many")
      .map(({ name }) => [name, enumeratedAndStored(source[name])])
  );

const asSavedProfile = (fields) => (profile) => ({
  ...profile,
  ...savedNestedItems(profile, fields),
  stored: true,
  modified: false,
});

const squashValuesInto = (record, values, fields) =>
  Object.fromEntries(
    fields.map(({ name, type, fields }, index) => [
      name,
      type !== "many"
        ? values[index]
        : [...(record[name] || []), nestedRecord(fields, values[index])],
    ])
  );

const nestedRecord = (fields, values) =>
  Object.fromEntries(fields.map(({ name }, index) => [name, values[index]]));

const asIts = (input) => input;

const groupValues = (fields, values) =>
  fields.map(({ type, parse = asIts, fields, idx=null }, index) =>
    type !== "many"
      ? parse(values[idx !== null ? idx : index])
      : groupValues(fields, values.slice(index))
  );

const intoARecordNamed = (name, fields) => (record, line) => {
  const values = line.split(/\s+/);
  return name !== values[0]
    ? record /* skip */
    : squashValuesInto(record, groupValues(fields, values), fields);
};

export const intoLines = (input) =>
  input.replace(/^\n*/, "").replace(/\n*$/, "").split("\n");

export const loadProfile =
  (name, fields = []) =>
  (response) => {
    const [_head, ...lines] = intoLines(response);
    return asSavedProfile(fields)(
      lines.reduce(intoARecordNamed(name, fields), {})
    );
  };

const genericLoadAll = ({ cliType }) => ifCl.run(`show profile ${cliType}`);

export const composeAPI = ({ loadAll = genericLoadAll, ...composition }) => ({
  load: () => loadAll(composition).then((response) => ({ response })),
  parseProfile: (name) => loadProfile(name, composition.fields),
  loadProfile: (name) =>
    loadAll(composition, name).then(loadProfile(name, composition.fields)),
  composeApplyCommand: (...args) => expressApplyCommand(composition, ...args),
  composeProfileSettings: (previous, { pristine, ...expected }) =>
    expressApplyCommand(
      composition,
      [previous],
      [{ ...expected, modified: pristine !== true }]
    ),
});
